/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { createSelector } from 'reselect';

export const getCurrentBrandId = (state) => state.ui.brandId;
export const getConfigByDomains = (state) => state.app.config.byDomain;

// Order Id
export const getOrderId = (state) => state.order.orderId;

// Auth
export const getAuthInfo = (state) => state.member.auth;
export const getUserType = (state) => state.member?.auth?.userType;
export const getAccessToken = (state) => state.member.auth.accessToken;
export const getExpiresAt = (state) => state.member.auth.expiresAt;
export const getIsAuthenticatedStatus = (state) => state.member.auth.isAuthenticated;
export const getLastVisitedPages = (state) => state.member.auth.lastVisitedPage;
export const getRememberConsentStatus = (state) => state.member.auth.rememberConsent;
export const getAuthError = (state) => state.member.auth.authError;
export const getAuthSuccess = (state) => state.member.auth.authSuccess;
export const getIsUserReAuthenticated = (state) => state.member?.auth?.reAuthenticatedUser || false;
export const getEnterpriseIdSelector = (state) => state.member.enterpriseId.enterpriseId;
export const getEnterpriseIdMessage = (state) => state.member.enterpriseId.enterpriseIdMessage;
export const getLastVisted = (state) => {
    if (state.member.auth.lastVisitedPage.length > 0) {
        const { lastVisitedPage } = state.member.auth;
        if (lastVisitedPage.indexOf('/shipping') >= 0) {
            return `/checkout/shipping/${getOrderId(state)}`;
        } if (lastVisitedPage.indexOf('/cart') >= 0) {
            return `/checkout/cart/${getOrderId(state)}`;
        } if (lastVisitedPage.indexOf('/payment') >= 0) {
            return `/checkout/payment/${getOrderId(state)}`;
        }
        return state.member.auth.lastVisitedPage;
    }
    return '/';
};

// Profile
export const getProfileInfo = (state) => state.member?.profile || {};
export const getUserRole = (state) => state.member?.profile?.userRole || '';
export const getFirstLastName = (state) => ({
    firstName: state.member?.profile?.firstName || '',
    lastName: state.member?.profile?.lastName || '',
});
export const getContactId = (state) => state.member?.profile?.contactId;

// AddressBook
export const getAddressBookEntries = (state) => state.member?.addressBook?.addressBookEntries || [];
export const getGuestAddressBookEntries = (state) => state.member?.addressBook?.guestAddressBookEntries || [];
export const getTotalEntriesAddressBook = (state) => state.member.addressBook.totalEntries;
export const getAddressBookPageNumber = (state) => {
    const { pageNumber } = state.member.addressBook;
    if (pageNumber) {
        return pageNumber;
    }
    return 0;
};
export const getTotalNoOfPages = (state) => state.member.addressBook.totalPages;
export const getBillingAddress = (state) => state.member.addressBook.billingAddress;

// Wallet
export const getWalletEntries = (state) => state.member.wallet.walletEntries || [];
export const getSavedCreditCardError = (state) => state.member.wallet.error.walletSaveCreditCard;
export const getCreditCardSavedFlag = (state) => state.member.wallet.creditCardSavedFlag;
export const getWalletCards = createSelector(
    [
        getWalletEntries,
    ],
    (walletCards) => walletCards.filter((card) => (!['PY', 'GP'].includes(card?.payType))),
);


// Member Id

export const getMemberId = (state) => {
    if (state.cart
        && state.cart.orderDetails
        && state.cart.orderDetails.fdOrderVO
        && state.cart.orderDetails.fdOrderVO.memberId) {
        return state.cart.orderDetails.fdOrderVO.memberId;
    }
    return null;
};

/**
 * @param {} state
 */
export const getBrandsById = (state) => {
    if (state && state.page && state.page.brands && state.page.brands.byId) {
        return state.page.brands.byId;
    }

    return null;
};

// Brand (Client Ids)
export const getBrand = createSelector(
    [
        getCurrentBrandId,
        getBrandsById,
    ],
    (currentBrandId, brandsById) => {
        if (currentBrandId && brandsById[currentBrandId]) {
            return brandsById[currentBrandId];
        }

        return null;
    },
);

/**
 * @param {} state
 */
export const getConfigByDomain = (state) => {
    if (state && state.app && state.app.config && state.app.config.byDomain) {
        return state.app.config.byDomain;
    }
    return null;
};

/**
 * @param {} state
 */
// Flags
export const getFlags = createSelector(
    [
        getBrand,
        getConfigByDomain,
    ],
    (brand, config) => {
        if (brand && brand.domain && config) {
            return config[brand.domain]['app-feature-flags'];
        }
        return {};
    },
);

// Check If Logged In user is Passport
export const getIsPassportUserLoggedIn = createSelector(
    [
        getUserType,
        getUserRole,
    ],
    (userType, userRole) => {
        if (userType === 'R' && userRole === 'P') {
            return true;
        }
        return false;
    },
);

/*
    * Check If user is remembered
    * Check if remembered and auth == false then redirect to login screen
*/
export const checkUserRemembered = (state) => {
    // const userProfile = getFirstLastName(state);
    const userProfile = getProfileInfo(state);
    const isAuthenticated = getIsAuthenticatedStatus(state);
    if (userProfile.email && !isAuthenticated) {
        return true;
    }
    return false;
};

/*
    * Show consent message to allow user to continue as guest or registered user
*/
export const showConcentForRememberUser = (state) => {
    const isRemembered = checkUserRemembered(state);
    const isRememberConsentShown = getRememberConsentStatus(state);
    if (isRemembered && !isRememberConsentShown) {
        return true;
    }
    return false;
};

const brandByDomain = {
    '18F': '1800flowers',
    HD: 'harryanddavid',
    '18B': '1800baskets',
    SCH: 'simplychocolate',
    FBQ: 'fruitbouquets',
    CCO: 'cheryls',
    PZU: 'personalizationuniverse',
    TPF: 'thepopcornfactory',
    WLF: 'wolfermans',
    SY: 'stockyards',
    BRY: 'berries',
    VC: 'vitalchoice',
    FL: 'florists',
    CI: 'cardisle',
    CSG: 'celebrations',
};

// Feature Flag
export const getFeatureFlags = createSelector(
    [getCurrentBrandId, getConfigByDomains],
    (currentBrandId, configCodes) => {
        if (
            currentBrandId
            && brandByDomain[currentBrandId]
            && configCodes[brandByDomain[currentBrandId]]
        ) {
            return configCodes[brandByDomain[currentBrandId]][
                'app-feature-flags'
            ];
        }

        return {};
    },
);

export const getCustomerEmail = (state) => {
    if (state && state.member && state.member.profile && state.member.profile.email) {
        return state.member.profile.email;
    }
    return '';
};

// Add to cart selectors
export const getAddToCartLoaderStatus = (state) => state.addToCart.loader;
export const getAddToCartError = (state) => state.addToCart.error;
export const getAddToCartErrorList = (state) => state.addToCart.errorList;
export const getAddToCartItemAdded = (state) => state.addToCart.itemAdded;
export const getAddToCartGiftMessage = (state) => state.addToCart.giftMessage;

// SSR selectors
export const getIsBot = (state) => state.app.ssr.config?.isBot;
export const getSSRHostname = (state) => state.app.ssr.config.hostname;
export const getSSRPath = (state) => state.app.ssr.config.path;
export const getSSRLoadingState = (state) => state.app.ssr.config.loaded;
export const getSSRDeviceType = (state) => state.app.ssr.config.deviceType;
export const getSSRConfig = (state) => state.app.ssr.config;
